/**
 * MidiSettingsModal Component
 * Device selector, real-time MIDI input monitor, audio test, and diagnostics
 */

import React, { useState } from 'react';
import { X, Radio, AlertCircle, CheckCircle2, RefreshCw, Volume2, Activity } from 'lucide-react';
import { MidiDeviceItem } from '../types/midi';
import { AudioEngine } from '../core/audioEngine';
import { getPitchName } from '../core/timing';

interface MidiSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  devices: MidiDeviceItem[];
  activeDeviceId: string | null;
  onSelectDevice: (id: string) => void;
  isSupported: boolean;
  onRefresh: () => void;
  audioEngine?: AudioEngine;
  lastMidiActivity?: {
    pitch: number;
    velocity: number;
    channel: number;
    type: 'on' | 'off';
    timestamp: number;
  } | null;
}

export const MidiSettingsModal: React.FC<MidiSettingsModalProps> = ({
  isOpen,
  onClose,
  devices,
  activeDeviceId,
  onSelectDevice,
  isSupported,
  onRefresh,
  audioEngine,
  lastMidiActivity,
}) => {
  const [isPlayingTest, setIsPlayingTest] = useState(false);

  if (!isOpen) return null;

  const handleTestAudio = async () => {
    if (!audioEngine) return;
    setIsPlayingTest(true);
    try {
      await audioEngine.resumeContext();
      // Play a gentle C Major chord (C4, E4, G4)
      const stopC = audioEngine.playNoteOn(60, 95);
      const stopE = audioEngine.playNoteOn(64, 90);
      const stopG = audioEngine.playNoteOn(67, 95);

      setTimeout(() => {
        stopC();
        stopE();
        stopG();
        setIsPlayingTest(false);
      }, 700);
    } catch {
      setIsPlayingTest(false);
    }
  };

  return (
    <div
      id="midi-settings-modal"
      className="fixed inset-0 z-50 bg-[#1B1B1B]/60 backdrop-blur-xs flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FFFFFF] border-2 border-[#1B1B1B] shadow-2xl max-w-lg w-full overflow-hidden text-[#1B1B1B]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-[#1B1B1B] bg-[#F8F7F4]">
          <div className="flex items-center gap-2">
            <Radio className="w-5 h-5 text-[#2563eb]" />
            <h2 className="text-sm font-bold text-[#1B1B1B]">MIDI Devices & Signal Diagnostics</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-[#1B1B1B]/60 hover:text-[#1B1B1B] hover:bg-[#F2F0EB]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Status Banner */}
          {isSupported ? (
            <div className="flex items-start gap-2.5 p-3 bg-emerald-50 border border-emerald-300 text-xs text-emerald-900">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-600" />
              <div>
                <p className="font-bold text-emerald-950">Web MIDI API Active & Ready</p>
                <p className="text-[11px] text-emerald-800/90 mt-0.5">
                  Browser has granted Web MIDI permission. All incoming Note On / Note Off messages are processed in real-time.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-300 text-xs text-amber-900">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" />
              <div>
                <p className="font-bold text-amber-950">Web MIDI API not available in this browser</p>
                <p className="text-[11px] text-amber-800 mt-0.5">
                  Safari and older browsers do not natively support Web MIDI. Use Google Chrome, Microsoft Edge, Brave, or Opera. You can still test full recording and piano roll editing using the on-screen virtual keyboard or computer keys!
                </p>
              </div>
            </div>
          )}

          {/* Real-time MIDI Input Signal Monitor */}
          <div className="p-3.5 bg-[#F8F7F4] border border-[#1B1B1B]/20 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-bold text-[#1B1B1B]">
                <Activity className="w-4 h-4 text-emerald-600" />
                <span>Live MIDI Signal Monitor</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                    lastMidiActivity && lastMidiActivity.type === 'on'
                      ? 'bg-emerald-500 ring-2 ring-emerald-300 scale-125'
                      : 'bg-[#1B1B1B]/30'
                  }`}
                />
                <span className="text-[10px] text-[#1B1B1B]/60 uppercase tracking-wider font-mono font-bold">
                  {lastMidiActivity && lastMidiActivity.type === 'on' ? 'RECEIVING' : 'STANDBY'}
                </span>
              </div>
            </div>

            <div className="bg-[#FFFFFF] p-2.5 border border-[#1B1B1B]/20 font-mono text-xs">
              {lastMidiActivity ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-emerald-700 font-semibold">
                  <span>
                    Event: {lastMidiActivity.type === 'on' ? 'Note On' : 'Note Off'}
                  </span>
                  <span>Note: {getPitchName(lastMidiActivity.pitch)} ({lastMidiActivity.pitch})</span>
                  <span>Vel: {lastMidiActivity.velocity}</span>
                  <span>Ch: {lastMidiActivity.channel}</span>
                </div>
              ) : (
                <p className="text-[#1B1B1B]/50 italic text-[11px]">
                  Play any key or touch a pad on your MIDI keyboard to test signal reception...
                </p>
              )}
            </div>
          </div>

          {/* Devices List */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-[#1B1B1B]">Connected MIDI Inputs</span>
              <button
                onClick={onRefresh}
                className="flex items-center gap-1 text-[11px] text-[#2563eb] hover:underline bg-[#FFFFFF] border border-[#1B1B1B]/20 px-2 py-1 transition-colors"
                title="Rescan connected MIDI devices"
              >
                <RefreshCw className="w-3 h-3" />
                <span>Rescan</span>
              </button>
            </div>

            {devices.length === 0 ? (
              <div className="p-4 bg-[#F8F7F4] border border-[#1B1B1B]/20 text-center text-xs text-[#1B1B1B]/60">
                No physical MIDI keyboards detected. Check that your USB cable is firmly plugged in and your keyboard is turned ON.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {devices.map((d) => {
                  const isSelected = d.id === activeDeviceId || (activeDeviceId === 'all' && d.id === 'all');
                  const isOmni = d.id === 'all';
                  return (
                    <button
                      key={d.id}
                      onClick={() => onSelectDevice(d.id)}
                      className={`w-full text-left p-2.5 border text-xs flex items-center justify-between transition-colors ${
                        isSelected
                          ? 'bg-[#2563eb]/10 border-[#2563eb] text-[#1B1B1B] shadow-xs'
                          : 'bg-[#FFFFFF] border-[#1B1B1B]/20 text-[#1B1B1B] hover:bg-[#F8F7F4]'
                      }`}
                    >
                      <div className="truncate pr-2">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold">{d.name}</span>
                          {isOmni && (
                            <span className="text-[9px] bg-emerald-50 text-emerald-800 border border-emerald-300 px-1.5 py-0.2 font-mono">
                              Auto
                            </span>
                          )}
                        </div>
                        {d.manufacturer && !isOmni && (
                          <span className="text-[10px] text-[#1B1B1B]/60">{d.manufacturer}</span>
                        )}
                      </div>
                      {isSelected && (
                        <span className="text-[10px] bg-[#2563eb] text-white px-2 py-0.5 font-bold shrink-0">
                          Active
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Test Audio Output */}
          <div className="pt-2 border-t border-[#1B1B1B]/20 flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-[#1B1B1B] block">Sound Output Test</span>
              <span className="text-[11px] text-[#1B1B1B]/60">Verify audio synthesis through your speakers/headphones</span>
            </div>
            <button
              onClick={handleTestAudio}
              disabled={isPlayingTest}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FFFFFF] hover:bg-[#F2F0EB] text-[#1B1B1B] border border-[#1B1B1B] text-xs font-semibold transition-colors disabled:opacity-50"
            >
              <Volume2 className="w-3.5 h-3.5 text-[#2563eb]" />
              <span>{isPlayingTest ? 'Playing...' : 'Test Sound'}</span>
            </button>
          </div>

          {/* Quick Troubleshooting Guide */}
          <div className="bg-[#F8F7F4] p-3 border border-[#1B1B1B]/20 text-[11px] text-[#1B1B1B]/70 space-y-1 leading-relaxed">
            <span className="font-bold text-[#1B1B1B] block mb-1">Troubleshooting Checklist:</span>
            <ul className="list-disc list-inside space-y-0.5">
              <li><strong className="text-[#1B1B1B]">No Sound?</strong> Click anywhere in the app to make sure Web Audio is unmuted by browser security.</li>
              <li><strong className="text-[#1B1B1B]">Multiple Ports?</strong> Leave <span className="text-[#2563eb] font-semibold">All MIDI Inputs (Omni)</span> selected. Keyboards like Arturia / Akai often separate Keys and DAW controls across different ports.</li>
              <li><strong className="text-[#1B1B1B]">Windows:</strong> Close other DAWs (FL Studio, Ableton, Cubase) that lock USB-MIDI ports exclusively.</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-5 py-3 border-t-2 border-[#1B1B1B] bg-[#F8F7F4]">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-[#1B1B1B] hover:bg-black text-white text-xs font-bold transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
